import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useBag } from '../hooks/useBag';
import { useBagMutations } from '../hooks/useBagMutations';
import { BagDetailHeader } from './BagDetailHeader';
import { BagSalesList } from './BagSalesList';
import { BagNotesField } from './BagNotesField';
import { BagAddress } from './BagAddress';
import { BagActionsBar } from './BagActionsBar';
import { BagShippingDialog } from './BagShippingDialog';
import type { BagDetail, BagDetailSale } from '../types';

type Props = {
  bagId: string | null;
  onClose: () => void;
};

const toBagDetailSales = (raw: unknown[]): BagDetailSale[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (raw as any[]).map((s: any) => ({
    id: s.id,
    numero: s.numero ?? null,
    total: s.total ?? null,
    payment_status: s.payment_status,
    created_at: s.created_at ?? null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sale_items: (s.sale_items ?? []).map((item: any) => ({
      quantity: item.quantity,
      products: item.products ? { name: item.products.name } : null,
    })),
  }));
};

const toDetailBag = (raw: unknown): BagDetail => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = raw as any;
  return {
    id: r.id,
    tenant_id: r.tenant_id,
    customer_id: r.customer_id,
    status: r.status,
    label_generated: r.label_generated,
    opened_at: r.opened_at,
    closed_at: r.closed_at ?? null,
    expires_at: r.expires_at ?? null,
    dispatched_at: r.dispatched_at ?? null,
    notes: r.notes ?? null,
    customer: r.customer ?? null,
    sales: toBagDetailSales(r.sales ?? []),
    shipments: r.shipments ?? [],
  };
};

export const BagDetailModal = ({ bagId, onClose }: Props) => {
  const { toast } = useToast();
  const { data: rawBag, isLoading, isError } = useBag(bagId);
  const mutations = useBagMutations(bagId ?? '');
  const [shippingOpen, setShippingOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleCloseBag = () => setConfirmCloseOpen(true);

  const confirmCloseBag = () => {
    setConfirmCloseOpen(false);
    mutations.close.mutate(undefined, {
      onSuccess: () => {
        toast({ title: 'Sacolinha fechada com sucesso.' });
      },
      onError: () => {
        toast({ title: 'Erro ao fechar sacolinha.', variant: 'destructive' });
      },
    });
  };

  const handleReopen = () => {
    mutations.reopen.mutate(undefined, {
      onSuccess: () => {
        toast({ title: 'Sacolinha reaberta com sucesso.' });
      },
      onError: (err) => {
        const message =
          err instanceof Error ? err.message : 'Erro ao reabrir sacolinha.';
        toast({ title: message, variant: 'destructive' });
      },
    });
  };

  const handleQuote = () => {
    setShippingOpen(true);
  };

  const bag = rawBag ? toDetailBag(rawBag) : null;

  return (
    <>
    <Dialog open={bagId != null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {isLoading && (
          <>
            <DialogTitle className="sr-only">Carregando sacolinha</DialogTitle>
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </>
        )}

        {isError && !isLoading && (
          <>
            <DialogTitle className="sr-only">Erro ao carregar sacolinha</DialogTitle>
            <div className="py-8 text-center text-sm text-destructive">
              Não foi possível carregar a sacolinha. Tente fechar e abrir novamente.
            </div>
          </>
        )}

        {!isLoading && !isError && bag && (
          <>
            <DialogHeader>
              <DialogTitle asChild>
                <div>
                  <BagDetailHeader bag={bag} />
                </div>
              </DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="flex flex-col gap-6 pt-2">
              <section>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Vendas
                </h3>
                <BagSalesList sales={bag.sales} />
              </section>

              <Separator />

              <section>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Endereço
                </h3>
                <BagAddress customer={bag.customer} />
              </section>

              <Separator />

              <section>
                <BagNotesField
                  initial={bag.notes ?? ''}
                  onSave={(notes) => mutations.updateNotes.mutate(notes)}
                />
              </section>

              <BagActionsBar
                bag={bag}
                onClose={handleCloseBag}
                onReopen={handleReopen}
                onQuote={handleQuote}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    <BagShippingDialog
      bagId={bagId}
      open={shippingOpen}
      onClose={() => setShippingOpen(false)}
    />
    <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Fechar sacolinha?</AlertDialogTitle>
          <AlertDialogDescription>
            A sacolinha não vai aceitar mais compras acumuladas. Você ainda poderá
            reabri-la antes de gerar a etiqueta.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={confirmCloseBag}>Fechar sacolinha</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
